import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signupService } from "../service/signup.service";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";

// Registration interface for form data
export interface Registration {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
}

// Yup schema for signup
const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  phoneNumber: yup
    .string()
    .max(15, "Phone number must be at most 15 characters")
    .required("Phone number is required"),
  email: yup
    .string()
    .email("Enter a valid email address")
    .required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])/,
      "Password must include at least one uppercase letter, one lowercase letter, and one special character"
    ),
});

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<Registration>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<Registration> = async (data) => {
    setIsLoading(true);
    try {
      // Do NOT send roleId or send only a valid roleId that exists in AspNetRoles table.
      // If your backend expects roleId, make sure it matches an existing role.
      // Example: If you have a default role, set it here:
      // roleId: "your-valid-role-id"
      const payload: Registration = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        password: data.password,
        // roleId: "valid-role-id" // <-- Only include if required and valid!
      };
      await signupService.registerUser(payload);
      Swal.fire({
        toast: true,
        position: "top-right",
        icon: "success",
        title: "Signup successful.",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
      navigate("/dashboard");
    } catch (error: any) {
      setIsLoading(false);
      // Show more details for 400 Bad Request
      let errorMsg = "User registration failed. Please try again later.";
      // Check for backend validation errors
      if (error?.response?.status === 400) {
        if (error?.response?.data?.errors?.$values) {
          const duplicate = error.response.data.errors.$values.find(
            (e: any) => e.code === "DuplicateUserName"
          );
          if (duplicate) {
            errorMsg = "A user with this email already exists.";
          } else if (error.response.data.errors.$values.length > 0) {
            errorMsg = error.response.data.errors.$values
              .map((e: any) => e.description || e.code)
              .join(", ");
          }
        } else if (error?.response?.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }
      Swal.fire({
        toast: true,
        position: "top-right",
        icon: "error",
        title: errorMsg,
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  // Fetch all registrations (example usage)
  const fetchRegistrations = async () => {
    try {
      const registrationsRaw = await signupService.getAll();
      const registrations: Registration[] = registrationsRaw.map(
        (reg: any) => ({
          firstName: reg.firstName ?? "",
          lastName: reg.lastName ?? "",
          email: reg.email ?? "",
          phoneNumber: reg.phoneNumber ?? "",
          password: reg.password ?? "",
        })
      );
      console.log("Fetched registrations:", registrations);
      // You can set this to state if you want to display it
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    }
  };

  // Optionally, call fetchRegistrations() in useEffect if you want to fetch on mount
  // React.useEffect(() => {
  //   fetchRegistrations();
  // }, []);

  return (
    <div className="content">
      <div className="content-body p-0">
        <div className="login-bg">
          <div className="container">
            <div className="d-flex justify-content-end">
              <form
                className="login-form"
                style={{ minWidth: 500, maxWidth: 700, width: "100%" }}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <div className="d-flex flex-column gap-2">
                  <h2 className="login-title">Sign up for an account</h2>
                  {/* First Name and Last Name side by side */}
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1 form-group d-flex flex-column">
                      <label htmlFor="firstName">First Name</label>
                      <InputText
                        id="firstName"
                        placeholder="First Name"
                        {...register("firstName")}
                        className={errors.firstName ? "p-invalid" : ""}
                        autoComplete="given-name"
                      />
                      {errors.firstName && (
                        <span className="text-danger">
                          {errors.firstName.message}
                        </span>
                      )}
                    </div>
                    <div className="flex-grow-1 form-group d-flex flex-column">
                      <label htmlFor="lastName">Last Name</label>
                      <InputText
                        id="lastName"
                        placeholder="Last Name"
                        {...register("lastName")}
                        className={errors.lastName ? "p-invalid" : ""}
                        autoComplete="family-name"
                      />
                      {errors.lastName && (
                        <span className="text-danger">
                          {errors.lastName.message}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Phone Number and Email side by side, below First Name */}
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1 form-group d-flex flex-column">
                      <label htmlFor="phoneNumber">Phone Number</label>
                      <InputText
                        id="phoneNumber"
                        placeholder="Phone Number"
                        maxLength={15}
                        {...register("phoneNumber")}
                        className={errors.phoneNumber ? "p-invalid" : ""}
                        autoComplete="tel"
                      />
                      {errors.phoneNumber && (
                        <span className="text-danger">
                          {errors.phoneNumber.message}
                        </span>
                      )}
                    </div>
                    <div className="flex-grow-1 form-group d-flex flex-column">
                      <label htmlFor="email">Email</label>
                      <InputText
                        id="email"
                        placeholder="Email"
                        type="email"
                        {...register("email")}
                        className={errors.email ? "p-invalid" : ""}
                        autoComplete="email"
                      />
                      {errors.email && (
                        <span className="text-danger">
                          {errors.email.message}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Password field */}
                  <div className="form-group d-flex flex-column position-relative">
                    <label htmlFor="password">Password</label>
                    <InputText
                      id="password"
                      type={isPasswordVisible ? "text" : "password"}
                      placeholder="Password"
                      {...register("password")}
                      className={`form-control pe-5 ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      autoComplete="new-password"
                    />
                    <i
                      className={`bi ${
                        isPasswordVisible ? "bi-eye" : "bi-eye-slash"
                      } text-dark position-absolute translate-middle-y end-0 me-4 cursor-pointer`}
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      style={{ top: "40px" }}
                      role="button"
                    ></i>
                    {errors.password && (
                      <span className="text-danger">
                        {errors.password.message}
                      </span>
                    )}
                  </div>
                  <Button
                    type="submit"
                    label={isLoading ? "Signing Up..." : "Sign Up"}
                    disabled={!isValid || isLoading || isSubmitting}
                    className="p-button-primary"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
